import { c as _c } from "react-compiler-runtime";
import { useCallback } from 'react';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import useCrmObjectTypeTracking from 'framework-listing-lib/internal/hooks/useCrmObjectTypeTracking';
import { alertFailure, alertSuccess } from 'framework-listing-lib/utils/alerts';
import useObjectTypeId from 'framework-listing-lib/internal/hooks/useObjectTypeId';
import { Metrics } from 'framework-listing-lib/internal/metrics';
import useApplicationId from 'framework-listing-lib/internal/hooks/useApplicationId';
import { useCurrentViewId, useViewsNamespace } from 'framework-listing-lib/internal/ViewTabs/hooks/useViewProps';
import { convertCustomerDataViewToView, convertViewUpdateToCustomerDataView, convertViewToCustomerDataView } from '../utils/mapper';
import { useUpdateSingleViewByIdMutation } from 'customer-data-views-management/singleView/hooks/useUpdateSingleViewByIdMutation';
import { useUpdateSingleDefaultViewMutation } from 'customer-data-views-management/singleView/hooks/useUpdateSingleDefaultViewMutation';
import { useFetchSingleView } from 'customer-data-views-management/singleView/hooks/useFetchSingleView';

// This should be correct enough to prevent render-time errors
// while the currentView loads
// but should also not overwrite any actual existing view
// if somehow the mutation is called during the loading state
import { jsx as _jsx } from "react/jsx-runtime";
const PLACEHOLDER_VIEW = {
  id: '',
  name: '',
  columns: [],
  state: {},
  filterGroups: [],
  quickFilters: [],
  quickFilterProperties: [],
  namespaceName: '',
  objectTypeId: '',
  ownerId: undefined,
  private: false,
  teamId: undefined,
  type: 'STANDARD'
};
export function useSaveView() {
  "use memo";

  const $ = _c(14);
  const {
    trackCrmTableInteraction
  } = useCrmObjectTypeTracking();
  const objectTypeId = useObjectTypeId();
  const namespace = useViewsNamespace();
  const currentViewId = useCurrentViewId();
  const applicationId = useApplicationId();
  const t0 = currentViewId || "";
  let t1;
  if ($[0] !== namespace || $[1] !== objectTypeId || $[2] !== t0) {
    t1 = {
      id: t0,
      objectTypeId,
      namespaceName: namespace
    };
    $[0] = namespace;
    $[1] = objectTypeId;
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    data: currentViewData
  } = useFetchSingleView(t1);
  const t2 = (currentViewData === null || currentViewData === void 0 ? void 0 : currentViewData.singleViewById) || PLACEHOLDER_VIEW;
  let t3;
  if ($[4] !== t2) {
    t3 = {
      view: t2
    };
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const [updateSingleViewMutation, t4] = useUpdateSingleViewByIdMutation(t3);
  const {
    loading
  } = t4;
  let t5;
  if ($[6] !== applicationId || $[7] !== namespace || $[8] !== trackCrmTableInteraction || $[9] !== updateSingleViewMutation) {
    t5 = viewUpdate => {
      if (!viewUpdate.id) {
        return Promise.resolve();
      }
      return updateSingleViewMutation({
        variables: convertViewUpdateToCustomerDataView({
          viewUpdate,
          namespace
        })
      }).then(response => {
        var _response$data, _response$data2;
        if (!response.data) {
          return;
        }
        trackCrmTableInteraction({
          action: "update-view",
          viewId: String(response.data.updateSingleView.id)
        });
        Metrics.counter("update-view-success-v4", {
          application_id: applicationId
        }).increment();
        alertSuccess({
          message: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
            message: "frameworkListingLib.viewTabs.alerts.viewUpdate.success.message",
            options: {
              name: (_response$data = response.data) === null || _response$data === void 0 ? void 0 : _response$data.updateSingleView.name
            }
          })
        });
        return convertCustomerDataViewToView((_response$data2 = response.data) === null || _response$data2 === void 0 ? void 0 : _response$data2.updateSingleView);
      }).catch(() => {
        Metrics.counter("update-view-failure-v4", {
          application_id: applicationId
        }).increment();
        alertFailure({
          message: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "frameworkListingLib.viewTabs.alerts.viewUpdate.failure.message"
          }),
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "frameworkListingLib.viewTabs.alerts.viewUpdate.failure.title"
          })
        });
      });
    };
    $[6] = applicationId;
    $[7] = namespace;
    $[8] = trackCrmTableInteraction;
    $[9] = updateSingleViewMutation;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const saveView = t5;
  let t6;
  if ($[11] !== loading || $[12] !== saveView) {
    t6 = {
      loading,
      saveView
    };
    $[11] = loading;
    $[12] = saveView;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
}
export function useSaveDefaultView() {
  "use memo";

  const $ = _c(12);
  const objectType = useObjectTypeId();
  const namespace = useViewsNamespace();
  const currentViewId = useCurrentViewId();
  const applicationId = useApplicationId();
  const t0 = currentViewId || "";
  let t1;
  if ($[0] !== namespace || $[1] !== objectType || $[2] !== t0) {
    t1 = {
      id: t0,
      objectTypeId: objectType,
      namespaceName: namespace
    };
    $[0] = namespace;
    $[1] = objectType;
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    data: currentViewData
  } = useFetchSingleView(t1);
  const t2 = (currentViewData === null || currentViewData === void 0 ? void 0 : currentViewData.singleViewById) || PLACEHOLDER_VIEW;
  let t3;
  if ($[4] !== t2) {
    t3 = {
      view: t2
    };
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const [updateDefaultViewMutation, t4] = useUpdateSingleDefaultViewMutation(t3);
  const {
    loading
  } = t4;
  let t5;
  if ($[6] !== applicationId || $[7] !== updateDefaultViewMutation) {
    t5 = view => updateDefaultViewMutation({
      variables: convertViewToCustomerDataView({
        view
      })
    }).then(() => {
      Metrics.counter("update-default-view-success", {
        application_id: applicationId
      }).increment();
    }).catch(() => {
      Metrics.counter("update-default-view-failure", {
        application_id: applicationId
      }).increment();
    });
    $[6] = applicationId;
    $[7] = updateDefaultViewMutation;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const saveDefaultView = t5;
  let t6;
  if ($[9] !== loading || $[10] !== saveDefaultView) {
    t6 = {
      loading,
      saveDefaultView
    };
    $[9] = loading;
    $[10] = saveDefaultView;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
}